import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export interface PageHeader {
  title: string;
  subtitle?: string | undefined;
  /** Optional controls rendered on the right of the topbar (before the standard icons). */
  actions?: ReactNode;
}

interface HeaderValue {
  header: PageHeader;
  setHeader(next: PageHeader): void;
}

const Context = createContext<HeaderValue | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [header, setHeader] = useState<PageHeader>({ title: "" });
  const value = useMemo(() => ({ header, setHeader }), [header]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function usePageHeaderValue(): PageHeader {
  return useContext(Context)?.header ?? { title: "" };
}

/** Pages declare the topbar title and subtitle the design shell shows above the content. */
export function usePageHeader(title: string, subtitle?: string, actions?: ReactNode): void {
  const context = useContext(Context);
  useEffect(() => {
    context?.setHeader({ title, subtitle, actions });
    document.title = title ? `${title} · Aventuria` : "Aventuria";
    // `actions` is a render tree; comparing it by identity would re-run every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, subtitle]);
}
